
import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';
import { Chip, Label, FakeMap } from '../components/common';
import { ethnicityLabel } from '../lib/identity';
import { Icon } from '../components/icons';
import { Portrait } from '../components/Portrait';
import { Img } from '../components/Img';

interface ProfilePreviewProps {
  userProfile: any;
  t: Theme;
  onBack: () => void;
}

function calcAge(dob: string): number {
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (today < new Date(today.getFullYear(), birth.getMonth(), birth.getDate())) age--;
  return age;
}

export function ProfilePreview({ userProfile, t, onBack }: ProfilePreviewProps) {
  const p = userProfile?.profile;
  const sparkAnswers = userProfile?.sparkAnswers || [];
  const photos = (p?.photos as string[]) || [];
  const age = p?.dateOfBirth ? calcAge(p.dateOfBirth) : null;
  const activities = p?.activityPreferences ? Object.keys(p.activityPreferences).sort((a, b) => p.activityPreferences[b] - p.activityPreferences[a]) : [];

  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 100,
      display: 'flex', flexDirection: 'column', background: t.paper,
    }}>
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 40 }} className="scroll-container">
        {/* Hero photo */}
        <div style={{ position: 'relative' }}>
          {photos[0] ? (
            <Img placeholder={t.paperAlt} src={photos[0]} alt="" style={{ width: '100%', height: 520, objectFit: 'cover' }} />
          ) : (
            <Portrait seed={userProfile?.id || 'me'} label={p?.displayName || 'You'} sub={p?.locationPostcode || ''} style={{ height: 520 }} />
          )}

          <button onClick={onBack} style={{
            all: 'unset', cursor: 'pointer',
            position: 'absolute', top: 12, left: 16,
            width: 40, height: 40, borderRadius: 100,
            background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{Icon.back({ s: 20, c: '#fff' })}</button>

          <div style={{ position: 'absolute', top: 12, right: 16, padding: '6px 12px', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(10px)', borderRadius: 100, color: '#fff', fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 1 }}>PREVIEW</div>

          {/* Photo dots */}
          {photos.length > 0 && (
            <div style={{ position: 'absolute', top: 12, left: 64, right: 90, display: 'flex', gap: 4 }}>
              {Array.from({ length: Math.max(photos.length, 1) }).map((_, i) => (
                <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i === 0 ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.35)' }} />
              ))}
            </div>
          )}

          {/* Name slab */}
          <div style={{
            position: 'absolute', left: 0, right: 0, bottom: 0,
            padding: '60px 24px 22px',
            background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
            color: '#fff',
          }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <h1 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontWeight: 400, fontSize: 44, margin: 0, lineHeight: 1, letterSpacing: -0.5 }}>
                {p?.displayName || 'Name'}
              </h1>
              {age && <span style={{ fontSize: 26, fontFamily: FONTS.sans, fontWeight: 300 }}>{age}</span>}
              <div style={{ flex: 1 }} />
            </div>
            {p?.job && <div style={{ marginTop: 8, fontFamily: FONTS.sans, fontSize: 14, opacity: 0.92 }}>{p.job}</div>}
          </div>
        </div>

        {/* Quick facts */}
        <div style={{ padding: '20px 20px 4px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {p?.locationPostcode && <Chip t={t}>{Icon.pin({ s: 11, c: t.inkSoft })} {p.locationPostcode}</Chip>}
          {p?.gender && <Chip t={t}>{p.gender.charAt(0).toUpperCase() + p.gender.slice(1)}</Chip>}
          {p?.relationshipGoal && <Chip t={t}>{p.relationshipGoal.replace(/_/g, ' ')}</Chip>}
        </div>

        {/* About */}
        {p?.bio && (
          <div style={{ padding: '22px 24px 8px' }}>
            <Label t={t}>About</Label>
            <p style={{
              fontFamily: FONTS.display, fontSize: 21, lineHeight: 1.35,
              color: t.ink, margin: '6px 0 0', fontWeight: 400, letterSpacing: -0.2,
            }}>
              "{p.bio}"
            </p>
          </div>
        )}

        {/* Identity chips (optional fields) */}
        {(() => {
          const chips = [ethnicityLabel(p?.ethnicity), p?.nationality].filter((v): v is string => !!v);
          return chips.length > 0 ? (
            <div data-clarity-mask="true" style={{ padding: '14px 24px 0', display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {chips.map((chip, i) => <Chip key={i} t={t}>{chip}</Chip>)}
            </div>
          ) : null;
        })()}

        {/* Interests */}
        {activities.length > 0 && (
          <div style={{ padding: '18px 24px 6px' }}>
            <Label t={t}>Into</Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 10 }}>
              {activities.map((x, i) => <Chip key={i} t={t} filled={i === 0}>{x}</Chip>)}
            </div>
          </div>
        )}

        {/* Second photo or first prompt */}
        {photos[1] && (
          <div style={{ padding: '20px 20px 4px' }}>
            <Img placeholder={t.paperAlt} src={photos[1]} alt="" style={{ width: '100%', height: 320, objectFit: 'cover', borderRadius: 20 }} />
          </div>
        )}

        {/* Prompt 1 */}
        {sparkAnswers[0] && (
          <div style={{ padding: '18px 24px' }}>
            <div style={{ borderRadius: 18, padding: '18px 20px', background: t.card, border: `1px solid ${t.line}` }}>
              <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7, color: t.inkMute, marginBottom: 6 }}>
                {sparkAnswers[0].prompt}
              </div>
              <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 22, lineHeight: 1.3, color: t.ink }}>
                &ldquo;{sparkAnswers[0].answer}&rdquo;
              </div>
            </div>
          </div>
        )}

        {/* Prompt 2 */}
        {sparkAnswers[1] && (
          <div style={{ padding: '6px 24px 12px' }}>
            <div style={{ borderRadius: 18, padding: '18px 20px', background: t.blush, color: '#1F1A17' }}>
              <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7, opacity: 0.65, marginBottom: 6 }}>
                {sparkAnswers[1].prompt}
              </div>
              <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 22, lineHeight: 1.3 }}>
                &ldquo;{sparkAnswers[1].answer}&rdquo;
              </div>
            </div>
          </div>
        )}

        {/* Additional photos */}
        {photos.slice(2).map((photo, idx) => (
          <div key={idx} style={{ padding: '20px 20px 4px' }}>
            <Img placeholder={t.paperAlt} src={photo} alt="" style={{ width: '100%', height: 320, objectFit: 'cover', borderRadius: 20 }} />
          </div>
        ))}

        {/* Map */}
        <div style={{ padding: '6px 24px 24px' }}>
          <Label t={t}>Local to</Label>
          <div style={{ marginTop: 10, borderRadius: 16, overflow: 'hidden', position: 'relative', background: t.paperAlt, height: 160, border: `1px solid ${t.line}` }}>
            <FakeMap t={t} neighborhood={p?.locationPostcode || 'London'} />
          </div>
        </div>

      </div>
    </div>
  );
}
